import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateTenant } from "../../../store/authSlice";
import clinicAPI from "../../../services/clinicApi";
import RegistrationLinksPanel from "../AdminSettings/RegistrationLinksPanel";
import { usePatientsBase } from "../../../utils/hubPaths";
import { useAdminAppMode, useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { HubScreen } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import s from "../clinic.module.css";
import { Button, useToast } from "../../ui";

export default function AdminClientsRegistration() {
  const navigate = useNavigate();
  const patientsBase = usePatientsBase();
  const isApp = useAdminHubUi();
  const isAdminApp = useAdminAppMode();
  const isMobile = useIsMobile(768);
  const isDesktopManage = isAdminApp && !isMobile;
  const toast = useToast();
  const dispatch = useDispatch();
  const [autoApprove, setAutoApprove] = useState(true);
  const [savingApprove, setSavingApprove] = useState(false);
  const [defaultUrl, setDefaultUrl] = useState("");

  useEffect(() => {
    clinicAPI
      .getTenantSettings()
      .then((data) => {
        const t = data.tenant || data;
        setAutoApprove(t.auto_approve_registrations !== false);
      })
      .catch(() => {});
    clinicAPI
      .getDefaultRegistrationLink()
      .then((data) => setDefaultUrl(data.link?.url || ""))
      .catch(() => {});
  }, []);

  const saveAutoApprove = async (next) => {
    setAutoApprove(next);
    setSavingApprove(true);
    try {
      const data = await clinicAPI.updateTenantSettings({
        auto_approve_registrations: next,
      });
      const t = data.tenant || data;
      dispatch(updateTenant(t));
      toast.success("Сохранено");
    } catch (e) {
      setAutoApprove(!next);
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSavingApprove(false);
    }
  };

  const copyDefault = async () => {
    if (!defaultUrl) return;
    try {
      await navigator.clipboard.writeText(defaultUrl);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const autoApproveControl = (
    <label
      className={
        isDesktopManage ? md.desktopRegToggle : s.settingsToggleRow
      }
    >
      <input
        type="checkbox"
        checked={autoApprove}
        disabled={savingApprove}
        onChange={(e) => saveAutoApprove(e.target.checked)}
      />
      <span>
        <strong>Автоподтверждение заявок</strong>
        <span className={isDesktopManage ? md.desktopRegToggleHint : s.branchCheckAddr}>
          {autoApprove
            ? "Регистрация по ссылке сразу активна"
            : "Заявки ждут одобрения в «На модерации»"}
        </span>
      </span>
    </label>
  );

  const desktopBody = (
    <div className={md.desktopClientsRegistration}>
      <p className={md.desktopRegIntro}>
        Публичные ссылки для самостоятельной записи. Клиент заполняет заявку —
        вы подтверждаете или включаете автоподтверждение ниже.
      </p>

      <div className={md.desktopRegTopCards}>
        {defaultUrl ? (
          <section className={md.desktopRegCard}>
            <div className={md.desktopRegCardHead}>
              <h3 className={md.desktopRegCardTitle}>Общая ссылка клуба</h3>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={copyDefault}
              >
                Копировать
              </Button>
            </div>
            <code className={md.desktopRegUrl}>{defaultUrl}</code>
          </section>
        ) : null}

        <section className={md.desktopRegCard}>{autoApproveControl}</section>
      </div>

      <RegistrationLinksPanel desktop />
    </div>
  );

  const mobileBody = (
    <div className={aa.clientsRegistrationPage}>
      <section className={aa.clientsRegistrationHero}>
        <div className={aa.clientsRegistrationHeroIcon} aria-hidden>
          🔗
        </div>
        <div className={aa.clientsRegistrationHeroText}>
          <h2 className={aa.clientsRegistrationHeroTitle}>Регистрация клиентов</h2>
          <p className={aa.clientsRegistrationHeroDesc}>
            Публичные ссылки для самостоятельной записи в группу. Если
            автоподтверждение выключено — заявки попадают в «На модерации» с
            указанием группы; после одобрения клиент получит приглашение задать
            пароль.
          </p>
        </div>
      </section>

      {defaultUrl ? (
        <div className={aa.clientsRegistrationQuick}>
          <div className={aa.clientsRegistrationQuickText}>
            <span className={aa.clientsRegistrationQuickLabel}>
              Общая ссылка клуба
            </span>
            <code className={aa.clientsRegistrationQuickUrl}>{defaultUrl}</code>
          </div>
          <Button type="button" size="sm" variant="secondary" onClick={copyDefault}>
            Копировать
          </Button>
        </div>
      ) : null}

      <div className={aa.clientsRegistrationToggle}>{autoApproveControl}</div>

      <RegistrationLinksPanel compact={isApp} />
    </div>
  );

  const body = isDesktopManage ? desktopBody : mobileBody;

  if (isApp) {
    return (
      <HubScreen
        title="Регистрация"
        onBack={() => navigate(patientsBase)}
        backLabel="Клиенты"
      >
        {body}
      </HubScreen>
    );
  }

  if (isDesktopManage) {
    return body;
  }

  return (
    <div className={aa.clientsHub}>
      <nav className={s.patientBreadcrumbs} aria-label="Навигация">
        <Link to={patientsBase}>Клиенты</Link>
        <span aria-hidden>/</span>
        <span>Регистрация</span>
      </nav>
      {body}
    </div>
  );
}
